import React from "react";

import { periodTriggerLabel } from "../../../pages/admin/uebersicht/uebersichtHelpers";
import { windowLabel } from "./financeSelectors";
import FinanceAgingCard from "./FinanceAgingCard";
import FinanceCashflowCard from "./FinanceCashflowCard";
import FinanceCostRankingCard from "./FinanceCostRankingCard";
import FinanceCostStructureCard from "./FinanceCostStructureCard";
import FinanceCurrentStateGrid from "./FinanceCurrentStateGrid";
import FinanceKpiGrid from "./FinanceKpiGrid";
import { useFinanceData } from "./useFinanceData";
import { currentBusinessPeriod } from "../../../utils/datetime";

/**
 * The Finanzen tab.
 *
 * Reads nothing from the legacy cost world. `/dashboard/profit` and
 * `/dashboard/kpis` fold every unrecognised cost row into one total and derive
 * a profit and a margin from it — in production that total carries a free-text
 * "Kaution" row as an operating expense, so every figure downstream of it was
 * wrong by an amount nobody could see. This tab is built on the A1 invoiced,
 * A2 cost-breakdown and revenue-analytics contracts instead, which classify
 * fail-closed and keep the unclassified remainder visible and separate.
 *
 * THE SELECTED PERIOD, WHATEVER ITS LENGTH (P3c)
 * ----------------------------------------------
 * These contracts used to answer for a single month, so this tab refused every
 * multi-month selection and fired nothing at all. P3a windowed all four, and the
 * tab now answers for the period the picker actually names — one month, a
 * bounded range, or a cross-year one. The folding happens in the services; there
 * is no loop over months here and no sum nobody specified.
 *
 * "Gesamte Zeit" is still refused, because every one of those contracts is
 * bounded at 24 months and an unbounded expected revenue has no window to fold
 * over. That refusal is now the ONLY one, and it costs the reader nothing they
 * could have trusted.
 *
 * TWO SECTIONS, TWO KINDS OF TRUTH
 * --------------------------------
 * Above: the selected period. Below: what is true right now — receivables open
 * across ALL rent months, and two deposit balances with no period at all. They
 * are separated by a rule and their own heading because a current-state figure
 * sitting under "Zeitraum · April – September 2026" silently claims to be that
 * period's, and that claim would be wrong for every card in the lower half.
 *
 * The lower section does NOT disappear when the period is one this tab cannot
 * answer for. Those figures never depended on the period, so hiding them would
 * be the same category error in the other direction.
 *
 * AND THE RECEIVABLES NO LONGER GO QUIET WITH IT (P3c). They ride inside the
 * `revenue-analytics` response, and that request used to be withheld entirely
 * for an unsupported period — so "Offene Forderungen" read "—" for data that was
 * sitting there and had nothing to do with the period. The hook now issues that
 * one request in every mode, with no period parameters when there is no window,
 * and reads only the current-state block from it.
 *
 * THE ANALYSIS BAND (F2c) sits under the KPI grid and inside the SAME period
 * section, because all three of its cards answer for the selected period: the
 * receipts chart, the cost structure and the per-unit cost ranking. It costs
 * exactly ONE additional request — `cashflow_trend` and the cost breakdown are
 * already in responses this tab fetches, so only the ranking is new.
 *
 * The cost structure MOVED here from the Übersicht in F2c rather than being
 * copied. The Übersicht is the compact summary; the split by bucket is analysis
 * and belongs beside the two cards reading the same A2 contract. It now has one
 * consumer, and a guard keeps it that way.
 */

const SECTION_LABEL =
  "text-[10px] font-medium uppercase tracking-[0.8px] text-muted-foreground";

function SectionHeading({ children }) {
  return (
    <div className="mb-[10px] flex items-center gap-2">
      <span className={SECTION_LABEL}>{children}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export default function FinanceTab({
  period,
  active = true,
  // Current state, owned by the page: it already loads this snapshot for the
  // overview tab and owns the details modal these props open. Taking them here
  // rather than fetching again is what keeps one live request and one modal.
  cashDeposits = null,
  cashDepositsLoading = false,
  onOpenCashDepositDetails,
}) {
  const {
    revenueAnalytics,
    companyInvoiced,
    costBreakdown,
    costRanking,
    window: periodWindow,
    unsupported,
    loading,
    reload,
    landlordDeposits,
    landlordLoading,
    reloadLandlordDeposits,
  } = useFinanceData(period, { enabled: active });

  // A closed tab renders nothing at all. The page mounts all four panels at
  // once, so without this the grid would already be standing there — and since
  // the hook reads nothing while closed, it would be standing there as five
  // em-dashes. Those are the panel's first painted frame on activation, which
  // says "we asked and got nothing" about data nobody has asked for yet.
  // (The hook is still called above: hooks run unconditionally, and its own
  // `enabled` gate is what keeps the requests from firing.)
  if (!active) return null;

  return (
    <div className="space-y-4" data-testid="company-finance-tab">
      <section
        data-testid="finance-period-section"
        aria-labelledby="finance-period-heading"
      >
        <SectionHeading>
          <span id="finance-period-heading">
            {/* The resolved window when there is one — `windowLabel` collapses an
                equal pair, so a single-month custom range does not read
                "August – August 2026"; otherwise the picker's own words, so the
                tab names what is actually selected. */}
            Zeitraum · {periodWindow ? windowLabel(periodWindow) : periodTriggerLabel(period)}
          </span>
        </SectionHeading>

        {periodWindow ? (
          <>
            <FinanceKpiGrid
              revenueAnalytics={revenueAnalytics}
              companyInvoiced={companyInvoiced}
              costBreakdown={costBreakdown}
              window={periodWindow}
              loading={loading}
              onRetry={reload}
            />

            {/* F2c. Inside the period section on purpose: every card here is the
                selected period's, and the rule below separates them from the
                figures that have no period at all. Each card reads its OWN
                source's `{data, error}`, so a dead cost service costs the two
                cost cards and leaves the receipts chart standing. */}
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <FinanceCashflowCard
                  revenueAnalytics={revenueAnalytics}
                  window={periodWindow}
                  currentPeriod={currentBusinessPeriod()}
                  loading={loading}
                  onRetry={reload}
                />
              </div>
              <div>
                <FinanceCostStructureCard
                  costBreakdown={costBreakdown}
                  window={periodWindow}
                  loading={loading}
                  onRetry={reload}
                />
              </div>
            </div>

            <div className="mt-4">
              <FinanceCostRankingCard
                costRanking={costRanking}
                window={periodWindow}
                loading={loading}
                onRetry={reload}
              />
            </div>
          </>
        ) : (
          <div
            data-testid="finance-period-unsupported"
            className="rounded-xl border border-border bg-card p-4 shadow-sm"
          >
            <p className="m-0 text-[13px] font-medium text-card-foreground">
              Für diesen Zeitraum liegen keine Finanzkennzahlen vor
            </p>
            <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
              Umsatz, Fakturierung und Betriebskosten sind für ausgewählte
              Zeiträume bis 24 Monate verfügbar. Bitte wähle einen Zeitraum.
              Forderungen und Kautionen unten bleiben unverändert sichtbar.
            </p>
          </div>
        )}
      </section>

      {/* §4: the break between the two kinds of truth is structural, not a gap.
          A rule, its own heading and its own as-of dates — see the docstring. */}
      <section
        data-testid="finance-current-state"
        aria-labelledby="finance-current-heading"
        className="border-t border-border pt-4"
      >
        <SectionHeading>
          <span id="finance-current-heading">Aktueller Stand · Forderungen &amp; Kapital</span>
        </SectionHeading>

        <div className="space-y-3">
          <FinanceCurrentStateGrid
            revenueAnalytics={revenueAnalytics}
            landlordDeposits={landlordDeposits}
            cashDeposits={cashDeposits}
            cashDepositsLoading={cashDepositsLoading}
            onOpenCashDepositDetails={onOpenCashDepositDetails}
            loading={loading}
            landlordLoading={landlordLoading}
            onRetry={reload}
            onRetryLandlord={reloadLandlordDeposits}
          />
          <FinanceAgingCard
            revenueAnalytics={revenueAnalytics}
            loading={loading}
            onRetry={reload}
          />
        </div>
      </section>
    </div>
  );
}
